import type { FunctionComponent, FormEvent, ChangeEvent } from 'react';
import React, { useState } from 'react';
import type { IWithGoogleReCaptchaProps } from 'react-google-recaptcha-v3';
import { useGoogleReCaptcha } from 'react-google-recaptcha-v3';
import TagManager from 'react-gtm-module';

import { Icon, Link, Title2 } from 'src/components/shared';
import Breadcrumb from 'src/components/shared/Breadcrumb';
import { Col, Container, Row, Spinner } from 'src/components/ui';
import Alert from 'src/components/ui/Alert';
import Form from 'src/components/ui/Form';
import Modal from 'src/components/ui/Modal/Modal';

import requestTypeOptions from '../../data/requestTypeOptions';
import InfoContactForm from '../infoContactForm/InfoContactForm';
import RequestContactForm from '../requestContactForm/RequestContactForm';
import SubjectContactForm from '../subjectContactForm/SubjectContactForm';
import TermsAndConditions from '../TermsAndConditions';
import style from './ContactForm.module.scss';

import { submitContactFormApi } from 'src/services/contact/submitContactFormApi';

import type { ContactFormData } from 'src/models/contact';

import { INVALID_INPUT_FORMAT, UNKNOWN_ERROR } from 'src/utils/Message.Utils';
import { mergeClassNames } from 'src/utils/ReactUtils';
import {
  isOrderNumberValid,
  isRegistrationPlateValid,
} from 'src/utils/ValidationUtils';

type ContactFormProps = {
  idNetworkBase?: string;
} & Partial<IWithGoogleReCaptchaProps>;

export type CustomValidityMessages = {
  registration: string;
  command: string;
};

export type UpdateFieldType = (
  event: ChangeEvent<
    HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement
  >,
  value?: string | number | boolean,
) => void;
type ApiResponseContact = 'initial' | 'success' | 'error';

const ContactForm: FunctionComponent<ContactFormProps> = ({
  idNetworkBase,
}) => {
  const gtmClickEvent = (requestSubject: string) => {
    TagManager.dataLayer({
      dataLayer: {
        event: 'eventGA',
        eventCategory: 'contact',
        eventAction: 'nous contacter',
        eventLabel: `formulaire soumis - ${requestSubject}`,
      },
    });
  };

  const initialCustomValidityMessages: CustomValidityMessages = {
    registration: '',
    command: '',
  };

  const [customValidityMessages, setCustomValidityMessages] =
    useState<CustomValidityMessages>(initialCustomValidityMessages);

  const { executeRecaptcha } = useGoogleReCaptcha();
  const [formValidation, setFormValidation] = useState(false);
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState<string[]>([]);

  const [apiContactResponse, setApiContactResponse] =
    useState<ApiResponseContact>('initial');
  const [formValue, setFormValue] = useState<ContactFormData>({
    ...(idNetworkBase && { requestType: 5 }),
    rendezVous: 1,
  } as ContactFormData);

  const registrationPlateValidation = (
    event: ChangeEvent<
      HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement
    >,
  ) => {
    if (event.target.name === 'registration') {
      if (!isRegistrationPlateValid(event.target.value)) {
        event.target.setCustomValidity(INVALID_INPUT_FORMAT);
        setCustomValidityMessages((prevState) => ({
          ...prevState,
          registration: INVALID_INPUT_FORMAT,
        }));
      } else {
        event.target.setCustomValidity('');
        setCustomValidityMessages((prevState) => ({
          ...prevState,
          registration: '',
        }));
      }
    }
  };

  const orderNumberValidation = (
    event: ChangeEvent<
      HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement
    >,
  ) => {
    if (event.target.name === 'command') {
      if (!isOrderNumberValid(event.target.value)) {
        event.target.setCustomValidity(INVALID_INPUT_FORMAT);
        setCustomValidityMessages((prevState) => ({
          ...prevState,
          command: INVALID_INPUT_FORMAT,
        }));
      } else {
        event.target.setCustomValidity('');
        setCustomValidityMessages((prevState) => ({
          ...prevState,
          command: '',
        }));
      }
    }
  };

  const updateField: UpdateFieldType = (event, value) => {
    registrationPlateValidation(event);
    orderNumberValidation(event);
    setFormValue({
      ...formValue,
      [event.target.name]: value ?? event.target.value,
    });
  };
  const prepareDataFormApi = (event: FormEvent<HTMLFormElement>) => {
    const formDataValue: ContactFormData = { ...formValue };
    const formControls = Array.from(
      (event.target as HTMLFormElement).elements,
    ) as HTMLInputElement[];
    const showedElementsForm = formControls
      .map((control) => control.name)
      .filter((key) => Object.keys(formDataValue).includes(key));
    for (const key in formDataValue) {
      if (!showedElementsForm.includes(key)) {
        delete formDataValue[key as never];
      }
    }
    const idBaseNetworkObj =
      idNetworkBase?.length && formDataValue.requestType === 5
        ? { idNetworkBase }
        : {};
    return { ...formDataValue, ...idBaseNetworkObj };
  };
  const checkRecaptchaAndSendData = async (
    event: FormEvent<HTMLFormElement>,
  ) => {
    if (executeRecaptcha) {
      const token = await executeRecaptcha('yourAction');
      if (!token) {
        setLoading(false);
        return;
      }
      try {
        const formDataValue = prepareDataFormApi(event);
        const data = await submitContactFormApi(formDataValue);
        if ('errors' in data) {
          setErrors(
            Object.values(data.errors).reduce(
              (prev, current) => prev.concat(current),
              [],
            ),
          );
          setApiContactResponse('error');
        } else {
          const requestSubject =
            requestTypeOptions(false)?.find(
              ({ id }) => id === formValue.requestType,
            )?.title || '';

          setFormValue({
            requestType: 0,
            accept: false,
          } as ContactFormData);
          setApiContactResponse('success');
          gtmClickEvent(requestSubject);
        }
      } catch (error) {
        setApiContactResponse('error');
      }
    }
  };
  const contactFormSubmitHandler = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (!event.currentTarget.checkValidity()) {
      event.stopPropagation();
      setFormValidation(true);
      return;
    }
    setLoading(true);
    checkRecaptchaAndSendData(event).then(() => {
      setLoading(false);
      window.scrollTo({
        top: 0,
        behavior: 'smooth',
      });
    });
  };
  const hideModal = () => {
    setFormValue({
      ...(idNetworkBase ? { requestType: 5 } : { requestType: 0 }),
      rendezVous: 1,
    } as ContactFormData);
  };
  const showModal = () =>
    [1, 2].includes(formValue.requestType) &&
    (formValue.rendezVous === 0 || formValue.subRequestType === 3);

  const getSuccessMessage = () => (
    <div className={style.success}>
      <Icon name="coche" height={48} width={48} className={style.successIcon} />
      <p>
        MERCI !<br /> Votre demande de contact a bien été prise en compte.
        <br /> A bientôt chez AD !
      </p>
    </div>
  );

  const getErrorMessage = () => (
    <Alert
      variant="danger"
      className={mergeClassNames([style.guaranteeForm, style.error])}
    >
      {errors.length ? (
        <ul>
          {errors.map((el, i) => (
            <li key={i}>{el}</li>
          ))}
        </ul>
      ) : (
        UNKNOWN_ERROR
      )}
    </Alert>
  );
  return (
    <>
      <main className={style.contact}>
        <Container>
          <Row>
            <Col lg="12">
              <div className={style.breadcrumbContainer}>
                <Breadcrumb
                  breadcrumbItems={[
                    {
                      title: 'Accueil',
                      targetUrl: '/',
                    },
                    {
                      title: 'Nous contacter',
                      targetUrl: null,
                    },
                  ]}
                />
              </div>
              <div className={style.contactHeader}>
                <h1 className={style.contactHeaderTitle1}>Nous contacter</h1>
                <Title2 className={style.contactHeaderTitle2}>
                  <h2>Pour toutes demandes, merci de remplir ce formulaire</h2>
                </Title2>
              </div>
              {apiContactResponse === 'success' ? (
                getSuccessMessage()
              ) : (
                <Form
                  name="contact"
                  method="post"
                  id="contact-form"
                  validated={formValidation}
                  onSubmit={contactFormSubmitHandler}
                  noValidate
                >
                  {apiContactResponse === 'error' && getErrorMessage()}
                  <Link className={style.backBtn} href="/" rel="noreferrer">
                    <Icon
                      name="fleche-g"
                      width={28}
                      height={28}
                      color="red"
                    ></Icon>
                  </Link>
                  <SubjectContactForm
                    updateField={updateField}
                    formValue={formValue}
                    idNetworkBase={idNetworkBase}
                  />
                  <RequestContactForm
                    updateField={updateField}
                    formValue={formValue}
                    setFormValue={setFormValue}
                    customValidityMessages={customValidityMessages}
                  />
                  <InfoContactForm
                    updateField={updateField}
                    formValue={formValue}
                  />
                  <TermsAndConditions
                    formValue={formValue}
                    updateField={updateField}
                    formValidation={formValidation}
                  >
                    {`J’accepte que mes informations soient exploitées dans le cadre de
    la prise de contact commerciale et de la relation qui peut en découler avec AD.`}
                  </TermsAndConditions>
                  <div className={style.contactFormSubmit}>
                    <button
                      type="submit"
                      className={style.contactFormSubmitBtn}
                      disabled={loading}
                    >
                      {loading && <Spinner className={style.spinner} />}
                      Envoyer
                    </button>
                  </div>
                </Form>
              )}
            </Col>
          </Row>
          <Modal isOpen={showModal()} className={style.modal}>
            <div className={style.modalGarage}>
              <Icon
                name="croix"
                height={32}
                width={32}
                className={style.modalClose}
                onClick={() => hideModal()}
              />
              <p className={style.modalText}>
                Madame, Monsieur,
                <br />
                Pour cette demande nous vous invitons à contacter directement le
                réparateur.
              </p>
              <Link href="/garage-auto" className={`${style.modalButton}`}>
                Rechercher Votre Garage
              </Link>
            </div>
          </Modal>
        </Container>
      </main>
    </>
  );
};

export default ContactForm;
